import { useEffect, useState } from "react";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Send, X, Plus, FileText, Loader2 } from "lucide-react";

const fmtDT = (iso) => (iso ? new Date(iso).toLocaleString("tr-TR", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "-");

export const ProposalDialog = ({ project, onClose }) => {
  const [amount, setAmount] = useState("");
  const [currency, setCurrency] = useState("TRY");
  const [note, setNote] = useState("");
  const [recips, setRecips] = useState([]);
  const [mName, setMName] = useState("");
  const [mEmail, setMEmail] = useState("");
  const [quick, setQuick] = useState([]);
  const [history, setHistory] = useState([]);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (!project) return;
    setAmount(""); setCurrency("TRY"); setNote(""); setRecips([]); setMName(""); setMEmail("");
    Promise.all([
      api.get("/settings/approvers").catch(() => ({ data: { approvers: [] } })),
      api.get("/approvals/recipients").catch(() => ({ data: [] })),
    ]).then(([a, u]) => {
      const seen = new Set();
      const list = [];
      (a.data.approvers || []).forEach((x) => { if (!seen.has(x.email)) { seen.add(x.email); list.push({ name: x.name, email: x.email, tag: "Kayıtlı" }); } });
      (u.data || []).forEach((x) => { if (!seen.has(x.email)) { seen.add(x.email); list.push({ name: x.name, email: x.email, tag: x.company }); } });
      setQuick(list);
    });
    api.get(`/proposals?project_id=${project.id}`).then((r) => setHistory(r.data)).catch(() => setHistory([]));
  }, [project]);

  const addRecip = (r) => {
    if (!r.email || !r.email.includes("@")) return toast.error("Geçerli bir e-posta girin");
    if (recips.some((x) => x.email === r.email)) return;
    setRecips((p) => [...p, r]);
  };

  const send = async () => {
    const amt = parseFloat(String(amount).replace(",", "."));
    if (!amt || amt <= 0) return toast.error("Geçerli bir tutar girin");
    if (!recips.length) return toast.error("En az bir alıcı ekleyin");
    setSending(true);
    try {
      const { data } = await api.post("/proposals/send", { project_id: project.id, amount: amt, currency, note, recipients: recips });
      if (data.sent.length) toast.success(`Teklif ${data.sent.length} alıcıya PDF ekiyle gönderildi`);
      if (data.failed.length) toast.error(`Gönderilemeyen: ${data.failed.join(", ")}`);
      onClose();
    } catch (e) { toast.error(apiError(e)); }
    setSending(false);
  };

  return (
    <Dialog open={!!project} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent aria-describedby={undefined} className="bg-white border-slate-200 rounded-none max-w-lg max-h-[90vh] overflow-y-auto" data-testid="proposal-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl font-bold tracking-tight flex items-center gap-2">
            <Send size={17} className="text-emerald-600" /> Teklif Gönder
          </DialogTitle>
        </DialogHeader>
        {project && (
          <div className="space-y-4">
            <p className="text-xs text-slate-500 -mt-1">
              <strong>{project.name}</strong> · {project.zone || "-"} · Stand {project.stand_no || "-"}
              {project.winner && <span className="block mt-0.5 text-emerald-600">Kazanan uygulayıcı: {project.winner.company}</span>}
            </p>
            <div className="grid grid-cols-3 gap-2">
              <div className="col-span-2 space-y-1.5">
                <Label>Teklif Tutarı *</Label>
                <Input type="number" min="0" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="örn. 1250000" data-testid="proposal-amount-input" className="rounded-none border-slate-200 h-11 font-semibold" />
              </div>
              <div className="space-y-1.5">
                <Label>Para Birimi</Label>
                <Select value={currency} onValueChange={setCurrency}>
                  <SelectTrigger data-testid="proposal-currency-select" className="rounded-none h-11 border-slate-200"><SelectValue /></SelectTrigger>
                  <SelectContent className="rounded-none">
                    <SelectItem value="TRY">TRY</SelectItem>
                    <SelectItem value="USD">USD</SelectItem>
                    <SelectItem value="EUR">EUR</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>Alıcılar * ({recips.length})</Label>
              {recips.length > 0 && (
                <div className="flex flex-wrap gap-1.5" data-testid="proposal-recipients">
                  {recips.map((r) => (
                    <span key={r.email} className="inline-flex items-center gap-1.5 bg-slate-100 border border-slate-200 px-2 py-1 text-xs" data-testid={`proposal-recipient-${r.email}`}>
                      {r.name ? `${r.name} — ` : ""}{r.email}
                      <button onClick={() => setRecips((p) => p.filter((x) => x.email !== r.email))} className="text-slate-400 hover:text-red-500" data-testid={`remove-recipient-${r.email}`}><X size={11} /></button>
                    </span>
                  ))}
                </div>
              )}
              {quick.length > 0 && (
                <Select value="" onValueChange={(v) => { const r = quick.find((x) => x.email === v); if (r) addRecip({ name: r.name, email: r.email }); }}>
                  <SelectTrigger data-testid="proposal-quick-select" className="rounded-none h-10 border-slate-200 text-slate-500">
                    <SelectValue placeholder="Listeden hızlı ekle..." />
                  </SelectTrigger>
                  <SelectContent className="rounded-none max-h-64">
                    {quick.filter((x) => !recips.some((r) => r.email === x.email)).map((x) => (
                      <SelectItem key={x.email} value={x.email}>{x.name} — {x.email}{x.tag ? ` (${x.tag})` : ""}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
              <div className="flex gap-2">
                <Input value={mName} onChange={(e) => setMName(e.target.value)} placeholder="Ad (opsiyonel)" data-testid="proposal-manual-name" className="rounded-none border-slate-200 h-10 w-36" />
                <Input value={mEmail} onChange={(e) => setMEmail(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { addRecip({ name: mName.trim(), email: mEmail.trim().toLowerCase() }); setMName(""); setMEmail(""); } }} placeholder="eposta@firma.com" data-testid="proposal-manual-email" className="rounded-none border-slate-200 h-10 flex-1" />
                <Button variant="outline" onClick={() => { addRecip({ name: mName.trim(), email: mEmail.trim().toLowerCase() }); setMName(""); setMEmail(""); }} data-testid="proposal-add-manual" className="rounded-none h-10 border-slate-200"><Plus size={14} /></Button>
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>Açıklama (opsiyonel — PDF'e ve e-postaya eklenir)</Label>
              <Textarea value={note} onChange={(e) => setNote(e.target.value)} rows={3} data-testid="proposal-note-input" className="rounded-none border-slate-200" />
            </div>
            <Button onClick={send} disabled={sending} data-testid="proposal-send-button" className="w-full rounded-none h-11 bg-slate-900 text-white hover:bg-slate-700 gap-2 font-semibold transition-colors duration-200">
              {sending ? <><Loader2 size={14} className="animate-spin" /> Gönderiliyor...</> : <><Send size={14} /> Teklifi PDF Ekiyle Gönder</>}
            </Button>
            {history.length > 0 && (
              <div className="border-t border-slate-100 pt-3 space-y-1.5" data-testid="proposal-history">
                <div className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">Daha Önce Gönderilenler</div>
                {history.map((h) => (
                  <div key={h.id} className="text-xs text-slate-500 flex items-start gap-1.5">
                    <FileText size={11} className="mt-0.5 shrink-0 text-slate-400" />
                    <span>
                      <strong className="text-slate-700">{Number(h.amount).toLocaleString("tr-TR")} {h.currency}</strong>
                      {" → "}{(h.recipients || []).map((r) => r.name || r.email).join(", ")}
                      <span className="block opacity-70">{fmtDT(h.created_at)} · {h.created_by_name}</span>
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
